import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  Button,
  DropdownV2 as Dropdown,
} from 'portal-commons';
import { createRequiredValidator } from '@/utils/validator';
import { SelectItem } from '@/types';
import { CampaignDetail, CampaignOperationItem } from '../../types';
import { campaignController } from '@/apis/core';
import { toastFlashMessage } from '@/utils';
import { getDcaOptions } from '@/apis/services';

interface Props {
  open: boolean;
  type: 'select' | 'update';
  campaign: Pick<CampaignDetail, 'uid' | 'usecase'>;
  carrier: CampaignOperationItem;
  provisional: boolean;
  onClose: (reload: boolean) => void;
}

interface FormValues {
  electedDcaUid: string;
}

const DcaSelectionModal: React.FC<Props> = ({
  open,
  type,
  campaign,
  carrier,
  provisional,
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [dcaOptions, setDcaOptions] = useState<SelectItem[]>([]);

  const fetchDcaOptions = async (params: Record<string, any>) => {
    setIsProcessing(true);
    const options = await getDcaOptions(params);
    if (options.length === 0) {
      setDcaOptions([
        {
          disabled: true,
          label:
            'Based on the use-case there is no Secondary DCA eligible for selection. Contact your CNP for details.',
          value: '0',
        },
      ]);
    } else {
      setDcaOptions(options);
    }
    setIsProcessing(false);
  };

  useEffect(() => {
    fetchDcaOptions({
      mnoId: carrier.networkId,
      soleProprietorEnabled:
        campaign.usecase === 'SOLE_PROPRIETOR' ? true : undefined,
      acceptCampaignShare: true,
      cnpMigrationSupported: provisional ? true : undefined,
    });
  }, [provisional, carrier, campaign]);

  const { control, reset, handleSubmit } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      electedDcaUid: '',
    },
  });

  const {
    field: { value: electedDcaUid, onChange: setElectedDcaUid },
    fieldState: { error: electedDcaUidError },
  } = useController({
    control,
    name: 'electedDcaUid',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a DCA'),
      },
    },
  });

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);
    const params = {
      mnoIds: [carrier.networkId],
      isProvisional: provisional,
    };
    const result = await campaignController.electSecondaryDca(
      {
        campaignUid: campaign.uid,
        electedDcaUid: data.electedDcaUid || '',
      },
      params
    );
    if (result) {
      const mnoName = carrier.rulesEngineResultsJson.mno;
      const dcaName = dcaOptions.find(
        (option) => option.value === data.electedDcaUid
      )?.label;
      toastFlashMessage(
        `${mnoName} successfully shared to ${dcaName}`,
        'success'
      );

      onClose(true);
    }

    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testid="dcaSelectionModal">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        {type === 'select' ? 'Select DCA' : 'Update Shared DCA'}
      </Box>
      <form noValidate onSubmit={handleSubmit(onValidHandler)}>
        <Flex sx={{ flexDirection: 'column', gap: 'm', mt: 15 }}>
          <Box
            as="span"
            sx={{
              fontSize: 'H300',
              fontWeight: 400,
              color: 't.black80',
              mb: '-1px',
            }}
          >
            Select a DCA to share campaign.
            <br />
            *Indicates a required field
          </Box>
          <Dropdown
            required
            disabled={isProcessing}
            data-testid="selectDcaDropdown"
            label="Select DCA"
            options={dcaOptions}
            error={!!electedDcaUidError?.message}
            helperText={electedDcaUidError?.message}
            value={electedDcaUid}
            onChange={(value: string) => {
              setElectedDcaUid(value);
            }}
          />
          <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
            <Button
              variant="outline"
              data-testid="dcaSelectionCancelButton"
              disabled={isProcessing}
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={isProcessing}
              data-testid="dcaSelectionSubmitButton"
              type="submit"
            >
              Share
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default DcaSelectionModal;
